import { Form, Input } from 'antd';
import { useEffect } from 'react';
import { FormModal } from '../form-modal/form-modal';
import { RedirectionTarget } from '@/services/redirection-targets-service/redirection-targets-service.types';
import { useTranslation } from 'react-i18next';
import { apiFieldErrors } from '@/utils/api-field-errors';
import { openNotification } from '@/utils/notification/notification';
import { DEFAULT_PHONE_PREFIX, isBlankPhone, phoneRule } from '@/utils/phone';

type RedirectionTargetModalProps = {
  open: boolean;
  onClose: () => void;
  /** Rejects with the API error so the modal can put its messages on the fields. */
  onSave: (values: RedirectionTargetFormValues) => Promise<void>;
  isLoading?: boolean;
  initialValues?: RedirectionTarget | null;
  mode: 'create' | 'edit';
};

export type RedirectionTargetFormValues = {
  name: string;
  phone: string;
  after_hours_phone?: string;
  aliases?: string;
  email?: string;
  extension?: string;
};

type FieldName = keyof RedirectionTargetFormValues;

const FIELDS: FieldName[] = ['name', 'phone', 'after_hours_phone', 'aliases', 'email', 'extension'];

// Matches the backend column; longer aliases are a 422.
const ALIASES_MAX_LENGTH = 255;

export function RedirectionTargetModal({
  open,
  onClose,
  onSave,
  isLoading = false,
  initialValues,
  mode,
}: RedirectionTargetModalProps) {
  const { t } = useTranslation(['settings', 'common']);
  const [form] = Form.useForm<RedirectionTargetFormValues>();

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && initialValues) {
        // Reads carry null, the form and write payload use undefined.
        form.setFieldsValue({
          name: initialValues.name,
          phone: initialValues.phone,
          after_hours_phone: initialValues.after_hours_phone || undefined,
          aliases: initialValues.aliases || undefined,
          email: initialValues.email || undefined,
          extension: initialValues.extension || undefined,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, mode, initialValues, form]);

  const serverMessage = (field: FieldName, messages: string[]) => {
    if (field === 'phone' || field === 'after_hours_phone') {
      return t('settings:redirectionTargets.modal.validation.phoneInvalid');
    }
    if (field === 'name' && messages.includes('has already been taken')) {
      return t('settings:redirectionTargets.modal.validation.nameTaken');
    }
    return messages[0];
  };

  const handleSubmit = async (values: RedirectionTargetFormValues) => {
    const afterHours = values.after_hours_phone;
    try {
      await onSave({
        ...values,
        // A bare "+48" left in the optional field means "no number"; "" clears a saved one.
        after_hours_phone: afterHours !== undefined && isBlankPhone(afterHours) ? '' : afterHours,
      });
    } catch (error) {
      const fields = Object.entries(apiFieldErrors(error) ?? {}).filter(
        (entry): entry is [FieldName, string[]] => FIELDS.includes(entry[0] as FieldName)
      );
      if (fields.length) {
        form.setFields(
          fields.map(([name, messages]) => ({ name, errors: [serverMessage(name, messages)] }))
        );
        return;
      }
      openNotification({
        message: t('settings:redirectionTargets.modal.saveFailed'),
        type: 'error',
      });
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <FormModal
      open={open}
      onCancel={handleCancel}
      onSubmit={() => form.submit()}
      isLoading={isLoading}
      title={
        mode === 'create'
          ? t('settings:redirectionTargets.modal.createTitle')
          : t('settings:redirectionTargets.modal.editTitle')
      }
      subtitle={
        mode === 'create'
          ? t('settings:redirectionTargets.modal.createSubtitle')
          : t('settings:redirectionTargets.modal.editSubtitle')
      }
      submitLabel={mode === 'create' ? t('common:actions.create') : t('common:actions.save')}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ phone: DEFAULT_PHONE_PREFIX }}
      >
        <Form.Item
          label={t('settings:redirectionTargets.modal.fields.name')}
          name="name"
          rules={[
            {
              required: true,
              message: t('settings:redirectionTargets.modal.validation.nameRequired'),
            },
          ]}
        >
          <Input placeholder={t('settings:redirectionTargets.modal.placeholders.name')} />
        </Form.Item>

        <Form.Item
          label={t('settings:redirectionTargets.modal.fields.phone')}
          name="phone"
          rules={[
            {
              required: true,
              message: t('settings:redirectionTargets.modal.validation.phoneRequired'),
            },
            phoneRule(t('settings:redirectionTargets.modal.validation.phoneInvalid')),
          ]}
        >
          <Input placeholder="+48 123 456 789" />
        </Form.Item>

        <Form.Item
          label={t('settings:redirectionTargets.modal.fields.aliases')}
          name="aliases"
          extra={t('settings:redirectionTargets.modal.help.aliases')}
        >
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 5 }}
            maxLength={ALIASES_MAX_LENGTH}
            showCount
            placeholder={t('settings:redirectionTargets.modal.placeholders.aliases')}
          />
        </Form.Item>

        <Form.Item
          label={t('settings:redirectionTargets.modal.fields.afterHoursPhone')}
          name="after_hours_phone"
          extra={t('settings:redirectionTargets.modal.help.afterHoursPhone')}
          rules={[
            phoneRule(t('settings:redirectionTargets.modal.validation.phoneInvalid'), {
              optional: true,
            }),
          ]}
        >
          <Input placeholder="+48 123 456 789" />
        </Form.Item>

        <Form.Item
          label={t('settings:redirectionTargets.modal.fields.email')}
          name="email"
          rules={[{ type: 'email', message: t('common:validation.emailInvalid') }]}
        >
          <Input placeholder="email@example.com" />
        </Form.Item>

        <Form.Item label={t('settings:redirectionTargets.modal.fields.extension')} name="extension">
          <Input placeholder="123" />
        </Form.Item>
      </Form>
    </FormModal>
  );
}
